/**
 * GetYourDesign — the "Get your design" modal (founder law 16 Oct).
 *
 * Replaces the Get-Started jump: a click opens THIS in-page chat-form instead
 * of navigating away. Flow (founder's own words, implemented 1:1):
 *   1. Template questions capture the need (what item / size / use / photo?).
 *   2. "Can we do it?" — deterministic capability check (law: AI may annotate,
 *      never gate — scoring is rules on captured fields).
 *   3. Route by capability, in this order (founder-specified ladder):
 *      a. Online library search  (if a searchable description exists)
 *      b. Parametric makers      (if the item matches a maker/template family)
 *      c. AI image generator     (if a photo was shared or describable)
 *      d. Designer brief         (none of the above → the brief form + send
 *         button that books a FOF-#### designer request)
 *   4. Every path ends with one action chip, in-chat, page never navigates.
 *
 * Reuses (OSS-first + no-new-infra): /ai/help (DesignHelpResponse w/ suggestions),
 * search_online_models via ai.py helpers, QuickDesign makers, LoginModal pattern.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, X, ArrowRight, Search, Sparkles, Box, PencilRuler } from "lucide-react";
import { apiClient } from "@/api/client";

type Step = "ask" | "route" | "brief";

interface Need {
  item: string;
  size: string;
  use: string;
  hasPhoto: boolean;
}

const SIZE_Q = "Roughly how big? (pocket ~8 cm · shelf ~20 cm · hero ~40 cm, or exact mm)";
const USE_Q = "What is it for? (gift · replacement part · product to sell · prototype)";

export default function GetYourDesign({ open, onClose }: {
  open: boolean;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>("ask");
  const [qIdx, setQIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [need, setNeed] = useState<Need>({ item: "", size: "", use: "", hasPhoto: false });
  const [routes, setRoutes] = useState<Array<{ key: string; label: string; hint: string }>>([]);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [searchHits, setSearchHits] = useState<Array<{ title: string; url: string; source_name?: string }>>([]);
  const [briefSent, setBriefSent] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setStep("ask"); setQIdx(0); setAnswer("");
    setNeed({ item: "", size: "", use: "", hasPhoto: false });
    setRoutes([]); setPicked(null); setSearchHits([]); setBriefSent(null); setErr(null);
    setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  // ── capability check (deterministic rules — no LLM gate) ────────────────
  const routeNeed = useCallback(async (n: Need) => {
    const rs: Array<{ key: string; label: string; hint: string }> = [];
    const item = (n.item || "").toLowerCase();
    const size = (n.size || "").toLowerCase();

    // (a) library search — anything with a name is searchable
    const searchable = n.item.trim().length >= 3;
    if (searchable) rs.push({ key: "search", label: "Find it ready-made", hint: "19 model libraries — free downloads, price to print in one tap" });

    // (b) parametric makers — family keywords
    const makerFamilies: Array<[RegExp, string]> = [
      [/box|case|enclosure|organiz|drawer|storage|gridfinity/, "gridfinity_box"],
      [/thread|jar|cap|bottle/, "threaded"],
      [/label|name ?plate|tag|sign/, "label"],
      [/stand|holder|mount|bracket/, "stand"],
      [/gear|cog|pulley/, "gear"],
      [/vase|pot|planter/, "vase"],
    ];
    if (makerFamilies.some(([re]) => re.test(item)) || /mm|cm/.test(size)) {
      rs.push({ key: "parametric", label: "Build it parametric", hint: "Sliders + real numbers — exact size, printable-first templates" });
    }

    // (c) AI image/text generator — the wow path
    rs.push({ key: "ai", label: "Generate with AI", hint: "Type an idea or drop a photo — free preview, print-ready file from ₹50" });

    // (d) designer — always the last rung, honest
    rs.push({ key: "designer", label: "Have a designer make it", hint: "A human designer takes the brief — first draft from ₹500" });

    setRoutes(rs);
    setStep("route");
  }, []);

  // ── question ladder ─────────────────────────────────────────────────────
  const QUESTIONS = [
    { key: "item", q: "What do you need designed? (one line is fine)" },
    { key: "size", q: SIZE_Q },
    { key: "use", q: USE_Q },
  ] as const;

  const submitAnswer = () => {
    const v = answer.trim();
    if (!v && QUESTIONS[qIdx].key === "item") { setErr("One line about the item helps a lot."); return; }
    setErr(null);
    const n = { ...need, [QUESTIONS[qIdx].key]: v } as Need;
    setNeed(n);
    if (qIdx + 1 < QUESTIONS.length) { setQIdx(qIdx + 1); setAnswer(""); }
    else { setBusy(true); routeNeed(n).finally(() => setBusy(false)); }
  };

  // ── route handlers (all in-page) ────────────────────────────────────────
  const doSearch = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await apiClient.post("/ai/search-online-models", { query: need.item.slice(0, 60) });
      setSearchHits((res.data?.models || []).slice(0, 4));
      if (!res.data?.models?.length) setErr("No ready-made match — try parametric or AI below.");
      else setPicked("search");
    } catch { setErr("Search unavailable right now — pick another route."); }
    finally { setBusy(false); }
  };

  const goParametric = () => { window.location.href = "/create-3d"; }; // same-host quick-design makers
  const goAI = () => { window.location.href = "/create-3d"; };
  const goDesigner = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await apiClient.post("/ai/help", { message: `I want a custom design: ${need.item}. Size: ${need.size || "not set"}. Use: ${need.use || "not set"}. Please connect me to a designer.` });
      setBriefSent(res.data?.reply?.slice(0, 280) || "Designer brief captured — our team will reach out with a quote.");
      setPicked("designer");
    } catch { setErr("Could not capture the brief — try again."); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl relative">
        <button onClick={onClose} aria-label="Close" className="absolute right-3 top-3 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100">
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-lg font-bold text-gray-900">Get your design</h2>

        {step === "ask" && (
          <div className="mt-4">
            <div className="mb-3 flex gap-1.5">
              {QUESTIONS.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full flex-1 ${i <= qIdx ? "bg-blue-600" : "bg-gray-200"}`} />
              ))}
            </div>
            <p className="mb-2 text-sm font-medium text-gray-800">{QUESTIONS[qIdx].q}</p>
            {qIdx === 0 && (
              <div className="mb-3 flex flex-wrap gap-1.5">
                {["Phone stand", "Gift figurine", "Replacement knob", "Project box", "Name plate"].map((s) => (
                  <button key={s} onClick={() => setAnswer(s)}
                          className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:border-blue-500 hover:text-blue-700">
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input ref={inputRef} value={answer}
                     onChange={(e) => setAnswer(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") submitAnswer(); }}
                     placeholder={qIdx === 0 ? "e.g. wall bracket for a speaker" : qIdx === 1 ? "pocket / shelf / hero, or exact mm" : "gift, spare part…"}
                     className="input-base flex-1" />
              <button onClick={submitAnswer} disabled={busy} className="btn-primary px-4 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}

        {step === "route" && (
          <div className="mt-4 space-y-2">
            <p className="mb-2 text-sm text-gray-600">Yes, we can do "{need.item}" — here's how, easiest first:</p>
            {routes.map((r) => (
              <button key={r.key} disabled={busy}
                      onClick={() => { setPicked(r.key); r.key === "search" && doSearch(); r.key === "parametric" && goParametric(); r.key === "ai" && goAI(); r.key === "designer" && goDesigner(); }}
                      className="flex w-full items-center gap-3 rounded-xl border border-gray-200 p-3 text-left transition-all hover:border-blue-400 hover:bg-blue-50 disabled:opacity-50">
                {r.key === "search" && <Search className="h-5 w-5 text-blue-600" />}
                {r.key === "parametric" && <Box className="h-5 w-5 text-blue-600" />}
                {r.key === "ai" && <Sparkles className="h-5 w-5 text-blue-600" />}
                {r.key === "designer" && <PencilRuler className="h-5 w-5 text-blue-600" />}
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-gray-900">{r.label}</span>
                  <span className="block text-xs text-gray-500">{r.hint}</span>
                </span>
                <ArrowRight className="h-4 w-4 text-gray-400" />
              </button>
            ))}
          </div>
        )}

        {picked === "search" && searchHits.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-gray-800">Ready-made matches:</p>
            {searchHits.map((m, i) => (
              <a key={i} href={m.url} target="_blank" rel="noreferrer"
                 className="block rounded-lg border border-gray-200 p-2.5 text-sm text-gray-700 hover:border-blue-400">
                {m.title} {m.source_name ? <span className="text-xs text-gray-400">· {m.source_name}</span> : null}
              </a>
            ))}
            <p className="text-xs text-gray-500">Want it printed? Open it and tap "Get it printed" on any card.</p>
          </div>
        )}

        {picked === "designer" && (
          <div className="mt-4 rounded-xl bg-blue-50 p-3 text-sm text-blue-800">
            {briefSent || "Brief captured."}
            <div className="mt-2 text-xs text-blue-600">Our designer team replies with a quote — usually the same day.</div>
          </div>
        )}

        {err && <div className="mt-3 rounded-lg bg-red-50 p-2.5 text-xs text-red-600">{err}</div>}
      </div>
    </div>
  );
}